import {
  LocationConditionRequest,
  LocationSettings,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { LocationData } from '../types'

const compareStringRule = (
  operator: 'contains' | 'doesNotContain' | '=' | '!=' | 'isAnyOf' | 'isNotAnyOf' | 'equals',
  ruleValue = '',
  value = ''
) => {
  switch (operator) {
    case 'contains':
      return value.includes(ruleValue)
    case 'doesNotContain':
      return !value.includes(ruleValue)
    case 'isAnyOf':
    case 'equals':
    case '=':
      return value === ruleValue
    case 'isNotAnyOf':
    case '!=':
      return value !== ruleValue
    default:
      throw new Error(`Unknown rule operator: ${operator}`)
  }
}

const compareNumberRule = (
  operator: '=' | '>' | '<' | '!=' | '<=' | '>=',
  ruleValue: number,
  value: number
) => {
  switch (operator) {
    case '=':
      return value === ruleValue
    case '>':
      return value > ruleValue
    case '<':
      return value < ruleValue
    case '!=':
      return value !== ruleValue
    case '<=':
      return value <= ruleValue
    case '>=':
      return value >= ruleValue
    default:
      throw new Error(`Unknown rule operator: ${operator}`)
  }
}

export const processPlaybooksLocationSettings = (
  locationSettings: Record<string, LocationSettings>,
  locationData: LocationData
) => {
  return Object.entries(locationSettings)
    .filter(([, locationRules]) => {
      if (locationRules.type === 'ShowToEveryone') {
        return true
      }
      return processGroup(locationRules.rules, locationData)
    })
    .map(([playbookId]) => playbookId)
}

const removeHttpPrefix = (url: string) => {
  return url.replace(/^(?:https?:\/\/)?(?:www\.)?/, '').replace(/\/$/, '')
}

const processGroup = (rule: LocationConditionRequest, locationData: LocationData): boolean => {
  const [originUrl, originQueryParams = ''] = locationData.originUrl.split('?') as [
    string,
    ...Array<string>,
  ]

  switch (rule.type) {
    case 'UrlValueCondition':
      return compareStringRule(
        rule.operator,
        removeHttpPrefix(rule.value.value),
        removeHttpPrefix(originUrl)
      )
    case 'QueryValueCondition':
      return compareStringRule(rule.operator, rule.value.value, originQueryParams)
    case 'PageValueCondition': {
      if (rule.value.type === 'PageScrollValue') {
        return compareNumberRule(rule.operator, rule.value.value, locationData.maxScrollPercentage)
      }
      const [time, unit] = rule.value.duration.split(' ') as [string, ...Array<string>]
      const timeInSeconds = (() => {
        if (unit === 'seconds') {
          return parseInt(time)
        }
        if (unit === 'minutes') {
          return parseInt(time) * 60
        }
        if (unit === 'hours') {
          return parseInt(time) * 3600
        }
        return 0
      })()
      return compareNumberRule(rule.operator, timeInSeconds, locationData.userTimeOnPage)
    }
    case 'LocationConditionGroup':
      if (rule.operator === 'and') {
        return rule.conditions.every(condition => processGroup(condition, locationData))
      }
      return rule.conditions.some(condition => processGroup(condition, locationData))
    case 'DeviceType':
      if (rule.operator === 'isAnyOf') {
        return !!rule.values?.some(ruleValue =>
          compareStringRule(rule.operator, ruleValue, locationData.deviceType)
        )
      }
      return !!rule.values?.every(ruleValue =>
        compareStringRule(rule.operator, ruleValue, locationData.deviceType)
      )
    case 'BrowserLanguage':
      if (rule.operator === 'isAnyOf') {
        return !!rule.values?.some(ruleValue =>
          compareStringRule(rule.operator, ruleValue, locationData.browserLanguage)
        )
      }
      if (rule.operator === 'equals') {
        return !!rule.values?.every(ruleValue =>
          compareStringRule(rule.operator, ruleValue, locationData.browserLanguage)
        )
      }
      return !!rule.values?.some(ruleValue =>
        compareStringRule(rule.operator, ruleValue, locationData.browserLanguage)
      )
    default:
      // Will error if not all cases are handled
      rule satisfies never
      return false
  }
}
